import { useMemo } from "react";
import { Battery, Cpu, Plug, Sun, Wrench } from "lucide-react";

import { useTargetableDevices } from "@/hooks/useAlarmRules";
import { cn } from "@/lib/cn";
import type { AlarmTargetableDevice } from "@/data/adapter";

interface Props {
  selectedIds: string[];
  onChange: (next: string[]) => void;
  /** Hide kinds (e.g. only show BESS for cell-imbalance rules). */
  allowedKinds?: ReadonlyArray<AlarmTargetableDevice["kind"]>;
}

const KIND_ICON: Record<AlarmTargetableDevice["kind"], React.ComponentType<{ className?: string }>> = {
  bess: Battery,
  grid: Plug,
  controller: Cpu,
  aux: Wrench,
  pv: Sun,
};

export function DeviceTargetingFields({ selectedIds, onChange, allowedKinds }: Props) {
  const { data, isLoading } = useTargetableDevices();
  const devices = useMemo(
    () =>
      (data ?? []).filter(
        (d) => !allowedKinds || allowedKinds.includes(d.kind),
      ),
    [data, allowedKinds],
  );

  const toggle = (id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((x) => x !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const allSelected =
    devices.length > 0 && devices.every((d) => selectedIds.includes(d.id));

  return (
    <div className="border border-edge rounded bg-surface-2/30 p-3">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-[12px] font-semibold">Apply to</div>
          <div className="text-[11px] text-text-faint">
            One rule per device; replicas share a template id.
          </div>
        </div>
        <button
          type="button"
          onClick={() => onChange(allSelected ? [] : devices.map((d) => d.id))}
          disabled={devices.length === 0}
          className="text-[10px] uppercase tracking-wide px-2 py-1 rounded border border-edge hover:bg-surface-2/60 disabled:opacity-40"
        >
          {allSelected ? "Clear" : "All"}
        </button>
      </div>
      {isLoading && (
        <div className="text-[12px] text-text-faint">Loading devices…</div>
      )}
      {!isLoading && devices.length === 0 && (
        <div className="text-[12px] text-text-faint">
          No devices match the rule's compatible kinds.
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {devices.map((d) => {
          const Icon = KIND_ICON[d.kind];
          const selected = selectedIds.includes(d.id);
          return (
            <button
              type="button"
              key={d.id}
              onClick={() => toggle(d.id)}
              className={cn(
                "flex items-center gap-1.5 px-2 py-1.5 rounded border text-[12px]",
                selected
                  ? "border-accent/60 bg-accent/15 text-accent"
                  : "border-edge bg-surface/40 text-text-faint hover:text-text",
              )}
            >
              <Icon className="size-3.5" />
              {d.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}
